// The Star-Spangled Banner (John Stafford Smith's melody, public domain), synthesised with Web Audio
// for the President's inauguration: a brass-like lead over a soft lower octave, in B♭ major, 3/4.
// It keeps playing under the final results; closing them (or muting) fades it out in under a second.

// [note, beats] — note names with octave, "r" for a rest. One beat = a quarter note.
type N = [string, number];
const PHRASE_A: N[] = [
  ["F4", .75], ["D4", .25],
  ["Bb3", 1], ["D4", 1], ["F4", 1],
  ["Bb4", 2], ["D5", .75], ["C5", .25],
  ["Bb4", 1], ["D4", 1], ["E4", 1],
  ["F4", 2], ["F4", .5], ["F4", .5],
  ["D5", 1.5], ["C5", .5], ["Bb4", 1],
  ["A4", 2], ["G4", .5], ["A4", .5],
  ["Bb4", 1], ["Bb4", 1], ["F4", 1],
  ["D4", 1], ["Bb3", 1],
];
const MELODY: N[] = [
  ...PHRASE_A, ...PHRASE_A,
  // And the rockets' red glare, the bombs bursting in air
  ["D5", .5], ["D5", .5],
  ["D5", 1], ["Eb5", 1], ["F5", 1],
  ["F5", 2], ["Eb5", .5], ["D5", .5],
  ["C5", 1], ["D5", 1], ["Eb5", 1],
  ["Eb5", 2], ["Eb5", 1],
  // Gave proof through the night that our flag was still there
  ["D5", 1.5], ["C5", .5], ["Bb4", 1],
  ["A4", 2], ["G4", .5], ["A4", .5],
  ["Bb4", 1], ["D4", 1], ["E4", 1],
  ["F4", 2], ["F4", 1],
  // O say does that star-spangled banner yet wave
  ["Bb4", 1], ["Bb4", 1], ["Bb4", .5], ["A4", .5],
  ["G4", 1], ["G4", 1], ["G4", 1],
  ["C5", 1], ["Eb5", .5], ["D5", .5], ["C5", .5], ["Bb4", .5],
  ["Bb4", 1], ["A4", 1], ["F4", .5], ["F4", .5],
  // O'er the land of the free and the home of the brave
  ["Bb4", 1.5], ["C5", .5], ["D5", .5], ["Eb5", .5],
  ["F5", 2], ["Bb4", .5], ["C5", .5],
  ["D5", 1.5], ["Eb5", .5], ["C5", 1],
  ["Bb4", 3.5],
];

const SEMI: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
function freq(n: string): number {
  const m = /^([A-G])(b|#)?(\d)$/.exec(n)!;
  const midi = 12 * (Number(m[3]) + 1) + SEMI[m[1]] + (m[2] === "b" ? -1 : m[2] === "#" ? 1 : 0);
  return 440 * 2 ** ((midi - 69) / 12);
}

let current: { ctx: AudioContext; out: GainNode; ends: number; watch: number } | null = null;

/** Start the anthem (stopping any earlier one). `volume` 0–1 follows the player's music volume;
 *  `stillAudible` is polled so muting part-way through fades it out. */
/** "O say can you see, by the dawn's early light" — the opening, for the capital's star landing. */
const OPENING: N[] = PHRASE_A.slice(0, 12);

/** `opening`: just the first phrase (about 6 s) instead of the whole anthem. */
export function playAnthem(ctx: AudioContext, volume: number, stillAudible: () => boolean, opening = false): void {
  stopAnthem(0.05);
  if (ctx.state === "suspended") void ctx.resume();
  const beat = 0.6, t0 = ctx.currentTime + 0.15;
  const out = ctx.createGain(); out.gain.value = 0.05 + 0.16 * volume;
  const tone = ctx.createBiquadFilter(); tone.type = "lowpass"; tone.frequency.value = 2600; tone.Q.value = 0.7;
  tone.connect(out).connect(ctx.destination);
  let t = t0;
  for (const [n, beats] of opening ? OPENING : MELODY) {
    const len = beats * beat;
    if (n !== "r") {
      const f = freq(n);
      for (const [mult, wave, gain] of [[1, "sawtooth", 0.5], [1.003, "square", 0.18], [0.5, "triangle", 0.45]] as [number, OscillatorType, number][]) {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = wave; o.frequency.value = f * mult;
        const hold = Math.max(0.06, len - 0.05);
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(gain, t + 0.035);
        g.gain.setTargetAtTime(gain * 0.72, t + 0.04, 0.12);
        g.gain.setValueAtTime(gain * 0.72, t + hold - 0.03);
        g.gain.linearRampToValueAtTime(0.0001, t + hold);
        o.connect(g).connect(tone); o.start(t); o.stop(t + hold + 0.02);
      }
    }
    t += len;
  }
  const ends = t;
  const watch = window.setInterval(() => {
    if (!current || current.out !== out) { clearInterval(watch); return; }
    if (ctx.currentTime > ends + 0.2) { clearInterval(watch); current = null; out.disconnect(); return; }
    if (!stillAudible()) stopAnthem(0.4);
  }, 250);
  current = { ctx, out, ends, watch };
}

/** Fade the anthem out over `secs` (default 0.8 s) and stop it. Safe to call when nothing is playing. */
export function stopAnthem(secs = 0.8): void {
  const c = current; if (!c) return;
  current = null; clearInterval(c.watch);
  const now = c.ctx.currentTime;
  try {
    c.out.gain.cancelScheduledValues(now);
    c.out.gain.setValueAtTime(c.out.gain.value, now);
    c.out.gain.linearRampToValueAtTime(0.0001, now + secs);
  } catch { /* ignore */ }
  setTimeout(() => { try { c.out.disconnect(); } catch { /* ignore */ } }, secs * 1000 + 60);
}

export const anthemPlaying = (): boolean => !!current;
